import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import { evaluate, evaluateValidationRule, getFormulaFunctions } from '../utils/formulaEvaluator';
import { FORMULA_CHALLENGES } from '../data/formulaChallenges';

function formatTime(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function formatValue(v) {
  if (v === null || v === undefined) return '(blank)';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

function Confetti({ show }) {
  if (!show) return null;
  const colors = ['#4f8ef7','#60a5fa','#e8eaf0','#a5b4fc'];
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999, overflow: 'hidden' }}>
      {Array.from({ length: 28 }).map((_, i) => (
        <div key={i} style={{ position: 'absolute', top: '-20px', left: `${Math.random() * 100}%`, width: `${5 + Math.random() * 7}px`, height: `${5 + Math.random() * 7}px`, background: colors[i % colors.length], borderRadius: i % 3 === 0 ? '50%' : '2px', animation: `fall ${1.2 + Math.random() * 0.8}s ${Math.random() * 0.5}s ease-in forwards` }} />
      ))}
      <style>{`@keyframes fall{0%{transform:translateY(0) rotate(0);opacity:1}100%{transform:translateY(100vh) rotate(720deg);opacity:0}}`}</style>
    </div>
  );
}

export default function FormulaChallenge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const c = TOKENS[theme];

  const challenge = FORMULA_CHALLENGES.find(ch => ch.id === id);
  const allInCategory = FORMULA_CHALLENGES;
  const currentIndex = allInCategory.findIndex(ch => ch.id === id);

  const [formula, setFormula] = useState(challenge?.starterFormula || '');
  const [results, setResults] = useState(null);
  const [showFunctions, setShowFunctions] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const editorRef = useRef(null);

  const functions = getFormulaFunctions();

  const diffStyle = {
    Easy:   { color: c.success, bg: theme === 'light' ? '#d1fae5' : '#051a10' },
    Medium: { color: c.warning, bg: theme === 'light' ? '#fef3c7' : '#1a1000' },
    Hard:   { color: c.error,   bg: theme === 'light' ? '#fee2e2' : '#1a0808' },
  };

  const typeColor = challenge?.type === 'validation' ? '#8b5cf6' : c.accent;
  const typeBg = challenge?.type === 'validation'
    ? (theme === 'light' ? '#ede9fe' : '#1a0a2e')
    : c.accentBg;

  useEffect(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(timerRef.current);
  }, [id]);

  useEffect(() => {
    if (challenge) setFormula(challenge.starterFormula || '');
    setResults(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
  }, [id, challenge]);

  if (!challenge) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.text }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ marginBottom: 16 }}>Challenge not found</p>
        <button onClick={() => navigate('/challenges/formula')} style={{ background: c.accent, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 7, cursor: 'pointer', fontWeight: 700 }}>Back to Formula Challenges</button>
      </div>
    </div>
  );

  const handleTest = () => {
    clearInterval(timerRef.current);
    const testResults = challenge.testCases.map(tc => {
      let got, error;
      if (challenge.type === 'validation') {
        const res = evaluateValidationRule(formula, tc.record, tc.priorRecord || null);
        got = res.fires;
        error = res.error;
      } else {
        const res = evaluate(formula, tc.record, tc.priorRecord || null);
        got = res.result;
        error = res.error;
      }

      let pass;
      if (error) {
        pass = false;
      } else if (challenge.type === 'formula') {
        // Numeric tolerance for float comparison
        if (typeof tc.expected === 'number' && typeof got === 'number') {
          pass = Math.abs(got - tc.expected) < 0.01;
        } else {
          pass = String(got).toLowerCase() === String(tc.expected).toLowerCase();
        }
      } else {
        pass = Boolean(got) === Boolean(tc.expected);
      }

      return { ...tc, got, pass, error };
    });

    const allPass = testResults.every(r => r.pass);
    setResults({ testResults, allPass });
    if (allPass) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2500);
    } else {
      // Restart timer
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    }
  };

  const insertAtCursor = (text) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const selection = editor.getSelection();
      const id = { major: 1, minor: 1 };
      const op = { identifier: id, range: selection, text, forceMoveMarkers: true };
      editor.executeEdits('insert', [op]);
      editor.focus();
    } else {
      setFormula(prev => prev + text);
    }
    setShowFunctions(false);
    setShowFields(false);
  };

  const ds = diffStyle[challenge.difficulty] || diffStyle.Easy;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: c.bg, fontFamily: "'Inter', -apple-system, sans-serif", color: c.text }}>

      <Confetti show={showConfetti} />

      {/* Navbar */}
      <nav style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', height: 52, background: c.navBg, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          {[['TrailCode', '/'], ['Formulas', '/challenges/formula'], [challenge.title, null]].map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: c.muted }}>›</span>}
              <span style={{ color: i === 2 ? c.secondary : c.muted, cursor: i < 2 ? 'pointer' : 'default' }}
                onClick={() => { if (crumb[1]) navigate(crumb[1]); }}>
                {crumb[0]}
              </span>
            </React.Fragment>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 12px', fontFamily: 'monospace', fontSize: 13, color: elapsed > 1800 ? c.warning : c.secondary }}>⏱ {formatTime(elapsed)}</div>
          <span style={{ color: ds.color, background: ds.bg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{challenge.difficulty}</span>
          <span style={{ color: typeColor, background: typeBg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
            {challenge.type === 'validation' ? '🛡 Validation Rule' : '⚡ Formula Field'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {user && <img src={user.user_metadata?.avatar_url} alt="avatar" style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${c.border}` }} />}
          <button onClick={() => navigate('/challenges/formula')} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← All Formula Challenges</button>
        </div>
      </nav>

      {/* 3-panel layout */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Panel 1: Problem */}
        <div style={{ width: '28%', borderRight: `1px solid ${c.border}`, overflowY: 'auto', padding: '20px', background: c.bg }}>
          <h1 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18, lineHeight: 1.4 }}>{challenge.title}</h1>

          {/* Description */}
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: 16, marginBottom: 14 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: c.accent, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 10px' }}>Scenario</p>
            <p style={{ fontSize: 13, color: c.secondary, lineHeight: 1.75, margin: 0 }}>{challenge.description}</p>
          </div>

          {/* Error message for validation rules */}
          {challenge.type === 'validation' && (
            <div style={{ background: theme === 'light' ? '#ede9fe' : '#1a0a2e', border: `1px solid ${typeColor}30`, borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: typeColor, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>Error Message to Show</p>
              <p style={{ fontSize: 12, color: theme === 'light' ? '#5b21b6' : '#c4b5fd', fontStyle: 'italic', margin: 0 }}>"{challenge.errorMessage}"</p>
            </div>
          )}

          {/* Return type for formula fields */}
          {challenge.type === 'formula' && challenge.returnType && (
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 16px', marginBottom: 14 }}>
              <span style={{ fontSize: 11, color: c.secondary }}>Return type: </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: c.accent }}>{challenge.returnType}</span>
            </div>
          )}

          {/* Available fields */}
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: 16, marginBottom: 14 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: c.accent, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>Available Fields</p>
            {challenge.fields.map((field, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < challenge.fields.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: c.text, fontFamily: 'monospace' }}>{field.name}</span>
                  <span style={{ fontSize: 10, color: c.muted, marginLeft: 8 }}>{field.type}</span>
                </div>
                <span style={{ fontSize: 11, color: c.secondary }}>e.g. {formatValue(field.example)}</span>
              </div>
            ))}
          </div>

          {/* Concept */}
          {challenge.concept && (
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: c.accent, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 10px' }}>Concept</p>
              <div style={{ fontSize: 12, color: c.secondary, lineHeight: 1.8, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{challenge.concept}</div>
              {challenge.docLink && (
                <a href={challenge.docLink} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 12, color: c.accent, fontSize: 11, textDecoration: 'none', border: `0.5px solid ${c.accent}`, padding: '4px 10px', borderRadius: 5 }}>Read docs →</a>
              )}
            </div>
          )}
        </div>

        {/* Panel 2: Formula Builder */}
        <div style={{ width: '42%', borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', background: theme === 'light' ? '#f8f9fd' : '#070810' }}>

          {/* Toolbar */}
          <div style={{ flexShrink: 0, borderBottom: `1px solid ${c.border}`, padding: '8px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: c.navBg }}>

            {/* Field picker */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setShowFields(!showFields); setShowFunctions(false); }} style={{ background: c.card, border: `1px solid ${c.border}`, color: c.text, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Insert Field ▾
              </button>
              {showFields && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, zIndex: 100, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                  {challenge.fields.map(f => (
                    <div key={f.name}
                      onClick={() => insertAtCursor(f.name)}
                      style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = c.cardHover}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: 13, fontFamily: 'monospace', color: c.text }}>{f.name}</span>
                      <span style={{ fontSize: 10, color: c.muted }}>{f.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Function picker */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setShowFunctions(!showFunctions); setShowFields(false); }} style={{ background: c.card, border: `1px solid ${c.border}`, color: c.text, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Insert Function ▾
              </button>
              {showFunctions && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, zIndex: 100, width: 380, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
                  {functions.map(group => (
                    <div key={group.category}>
                      <div
                        onClick={() => setActiveCategory(activeCategory === group.category ? null : group.category)}
                        style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, color: c.accent, background: c.bgAlt, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between' }}>
                        {group.category}
                        <span>{activeCategory === group.category ? '▲' : '▼'}</span>
                      </div>
                      {(activeCategory === group.category || activeCategory === null) && group.fns.map(fn => (
                        <div key={fn.name}
                          onClick={() => insertAtCursor(fn.name + '(')}
                          style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: `1px solid ${c.border}`, transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = c.cardHover}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: c.accent, fontFamily: 'monospace' }}>{fn.name}</span>
                          </div>
                          <div style={{ fontSize: 11, color: c.muted, fontFamily: 'monospace', marginBottom: 2 }}>{fn.syntax}</div>
                          <div style={{ fontSize: 11, color: c.secondary }}>{fn.desc}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <span style={{ fontSize: 11, color: c.muted, marginLeft: 'auto' }}>
              {challenge.type === 'validation' ? 'Return TRUE to block · FALSE to allow' : `Returns: ${challenge.returnType}`}
            </span>
          </div>

          {/* Monaco editor */}
          <div style={{ flex: 1 }}>
            <Editor
              key={`formula-${id}-${theme}`}
              height="100%"
              defaultLanguage="javascript"
              value={formula}
              theme={theme === 'light' ? 'vs' : 'vs-dark'}
              onChange={v => setFormula(v || '')}
              onMount={(editor) => { editorRef.current = editor; }}
              options={{
                fontSize: 14,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                automaticLayout: true,
                padding: { top: 16 },
                lineNumbers: 'on',
                wordWrap: 'on',
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                suggest: { showKeywords: false },
              }}
            />
          </div>

          {/* Test button */}
          <div style={{ flexShrink: 0, borderTop: `1px solid ${c.border}`, padding: '12px 16px' }}>
            <button onClick={handleTest} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '12px 28px', borderRadius: 7, cursor: 'pointer', fontSize: 14, fontWeight: 700, width: '100%', transition: 'all 0.15s' }}
              onMouseEnter={e => e.target.style.opacity = '0.88'}
              onMouseLeave={e => e.target.style.opacity = '1'}>
              ▶ Test Formula
            </button>
          </div>
        </div>

        {/* Panel 3: Test Results */}
        <div style={{ width: '30%', overflowY: 'auto', background: c.bg, display: 'flex', flexDirection: 'column' }}>

          {/* Success banner */}
          {results?.allPass && (
            <div style={{ margin: 16, background: theme === 'light' ? '#d1fae5' : '#051a10', border: `1px solid ${theme === 'light' ? '#6ee7b7' : '#065f46'}`, borderRadius: 10, padding: 18, textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>☁️</div>
              <p style={{ color: c.success, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>All tests passed!</p>
              <p style={{ color: c.success, fontSize: 12, opacity: 0.8, marginBottom: 8 }}>{challenge.testCases.length}/{challenge.testCases.length} records correct</p>
              <p style={{ color: c.secondary, fontSize: 11 }}>⏱ Solved in {formatTime(elapsed)}</p>
            </div>
          )}

          {/* Test results table */}
          {results && (
            <div style={{ padding: '0 16px 16px' }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: c.secondary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
                Test Records — {results.testResults.filter(r => r.pass).length}/{results.testResults.length} passed
              </p>

              {results.testResults.map((r, i) => {
                const isValidation = challenge.type === 'validation';
                const expectedLabel = isValidation
                  ? (r.expected ? '🔴 BLOCK' : '🟢 ALLOW')
                  : formatValue(r.expected);
                const gotLabel = isValidation
                  ? (r.got ? '🔴 BLOCK' : '🟢 ALLOW')
                  : (r.error ? `⚠ ${r.error}` : formatValue(r.got));

                return (
                  <div key={i} style={{ background: r.pass ? (theme === 'light' ? '#f0fdf4' : '#021a0e') : (theme === 'light' ? '#fef2f2' : '#1a0000'), border: `1px solid ${r.pass ? (theme === 'light' ? '#86efac' : '#065f46') : (theme === 'light' ? '#fca5a5' : '#3a0000')}`, borderRadius: 8, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: c.text }}>{r.pass ? '✅' : '❌'} {r.label}</span>
                    </div>

                    {/* Field values */}
                    <div style={{ background: c.card, borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                      {Object.entries(r.record).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', color: c.secondary }}>
                          <span style={{ fontFamily: 'monospace', color: c.accent }}>{k}</span>
                          <span>{formatValue(v)}</span>
                        </div>
                      ))}
                      {r.priorRecord && (
                        <>
                          <div style={{ borderTop: `1px dashed ${c.border}`, margin: '6px 0', fontSize: 10, color: c.muted }}>Prior values:</div>
                          {Object.entries(r.priorRecord).map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', color: c.muted }}>
                              <span style={{ fontFamily: 'monospace' }}>{k} (prior)</span>
                              <span>{formatValue(v)}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                      <div style={{ flex: 1, background: c.bg, borderRadius: 5, padding: '6px 10px' }}>
                        <div style={{ color: c.muted, marginBottom: 2 }}>Expected</div>
                        <div style={{ fontWeight: 600, color: c.success, fontFamily: 'monospace' }}>{expectedLabel}</div>
                      </div>
                      <div style={{ flex: 1, background: c.bg, borderRadius: 5, padding: '6px 10px' }}>
                        <div style={{ color: c.muted, marginBottom: 2 }}>Your formula</div>
                        <div style={{ fontWeight: 600, color: r.pass ? c.success : c.error, fontFamily: 'monospace' }}>{gotLabel}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* No results yet */}
          {!results && (
            <div style={{ padding: 24, textAlign: 'center', color: c.muted, fontSize: 13 }}>
              <p style={{ fontSize: 32, marginBottom: 12 }}>⚡</p>
              <p>Write your {challenge.type === 'validation' ? 'validation rule' : 'formula'} and click</p>
              <p style={{ color: c.accent, fontWeight: 600 }}>▶ Test Formula</p>
              <p style={{ marginTop: 12 }}>to see how it performs against {challenge.testCases.length} test records</p>
            </div>
          )}

          {/* Navigation */}
          <div style={{ marginTop: 'auto', padding: 16, borderTop: `1px solid ${c.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 11, color: c.muted }}>
              <span>{currentIndex + 1} / {allInCategory.length}</span>
              <span>{challenge.topic}</span>
            </div>
            <div style={{ background: c.border, borderRadius: 2, height: 2, marginBottom: 10 }}>
              <div style={{ width: `${((currentIndex + 1) / allInCategory.length) * 100}%`, height: 2, background: c.accent, borderRadius: 2, transition: 'width 0.3s' }} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => currentIndex > 0 && navigate(`/challenges/formula/${allInCategory[currentIndex - 1].id}`)}
                disabled={currentIndex <= 0}
                style={{ flex: 1, background: 'transparent', border: `1px solid ${c.border}`, color: currentIndex <= 0 ? c.muted : c.secondary, padding: 8, borderRadius: 6, cursor: currentIndex <= 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                ← Prev
              </button>
              <button
                onClick={() => currentIndex < allInCategory.length - 1 && navigate(`/challenges/formula/${allInCategory[currentIndex + 1].id}`)}
                disabled={currentIndex >= allInCategory.length - 1}
                style={{ flex: 1, background: 'transparent', border: `1px solid ${c.border}`, color: currentIndex >= allInCategory.length - 1 ? c.muted : c.secondary, padding: 8, borderRadius: 6, cursor: currentIndex >= allInCategory.length - 1 ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                Next →
              </button>
            </div>
            <button onClick={() => navigate('/challenges/formula')} style={{ width: '100%', background: 'transparent', border: 'none', color: c.muted, padding: 6, cursor: 'pointer', fontSize: 12, marginTop: 4 }}>
              ← All Formula Challenges
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}